import { useState, useCallback } from "react";
import {
    Box,
    Paper,
    Typography,
    IconButton,
    Tooltip,
    useTheme,
} from "@mui/material";
import { Link } from "@solar-icons/react/ssr";
import AiCanvases from "@/ai/AiCanvases";

/**
 * CanvasPanel — renders the multi-canvas editor for brief/wrap report workflows.
 *
 * Props:
 *  - canvasUpdates: Array<{ id, name?, timestamp }> — list of canvases created by the agent
 *  - newCanvasData: string | null — live streamed markdown content
 *  - canvasLoading: boolean — initial loading state
 *  - onChange: (markdown) => void — called when user edits canvas content
 *  - onLoad: (canvas) => void — called when a canvas finishes loading
 *  - onCanvasSelected: (canvas) => void — called when user switches canvas tabs
 */
export default function CanvasPanel({
    canvasUpdates = [],
    newCanvasData,
    canvasLoading,
    onChange,
    onLoad,
    onCanvasSelected,
}) {
    const theme = useTheme();
    const [activeCanvasId, setActiveCanvasId] = useState(null);
    const [copied, setCopied] = useState(false);

    const handleCanvasSelected = useCallback(
        (canvas) => {
            setActiveCanvasId(canvas?.id || null);
            onCanvasSelected?.(canvas);
        },
        [onCanvasSelected],
    );

    const handleCopyUrl = () => {
        if (!activeCanvasId) return;
        const url = `${window.location.origin}/canvas/${activeCanvasId}`;
        navigator.clipboard.writeText(url).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        });
    };

    return (
        <Paper
            sx={{
                height: "100%",
                display: "flex",
                flexDirection: "column",
                borderRadius: 3,
                background: theme.palette.background.default,
                overflow: "hidden",
            }}
        >
            <Box
                sx={{
                    px: 2,
                    pt: 2,
                    pb: 1,
                    display: "flex",
                    alignItems: "flex-start",
                    justifyContent: "space-between",
                }}
            >
                <Box>
                    <Typography color="text.secondary">Canvas</Typography>
                    <Typography variant="caption" color="text.secondary">
                        Created by a robot. Please verify with a human.
                    </Typography>
                </Box>
                {activeCanvasId && (
                    <Tooltip title={copied ? "Copied!" : "Copy canvas link"}>
                        <IconButton size="small" onClick={handleCopyUrl}>
                            <Link size={18} />
                        </IconButton>
                    </Tooltip>
                )}
            </Box>

            <Box sx={{ flex: 1, overflow: "auto", px: 2, pb: 2 }}>
                <AiCanvases
                    canvasUpdates={canvasUpdates}
                    newCanvasData={newCanvasData}
                    canvasLoading={canvasLoading}
                    onChange={onChange}
                    onLoad={onLoad}
                    onCanvasSelected={handleCanvasSelected}
                />
            </Box>
        </Paper>
    );
}
